import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, Validators } from '@angular/forms';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { Observable, catchError, delay, map, of, switchMap, tap } from 'rxjs';
import { ApiSdkService } from 'src/app/services/api-sdk.service';

@Component({
  selector: 'app-solo-input',
  templateUrl: './solo-input.component.html',
  styleUrls: ['./solo-input.component.scss'],
})
export class SoloInputComponent implements OnInit, OnChanges {
  @Input() onboardingFlowStep: any; // Consider typing this properly
  @Output() onChangeValues: EventEmitter<string> = new EventEmitter<string>();

  inputControl: FormControl;
  inputLoader = false;

  constructor(
    private recaptchaV3Service: ReCaptchaV3Service,
    private apiSdkService: ApiSdkService
  ) {
    this.inputControl = new FormControl('', {
      asyncValidators: [],
      updateOn: 'change'
    }); // Initialize with empty value and no validators
  }

  ngOnInit() {
    this.setupValidation();
    this.inputControl.valueChanges.subscribe(value => {
      if (this.onboardingFlowStep.validation?.totalumName) {
        this.totalumNameSetter(value);
      }

      if (this.inputControl.valid) {
        this.onChangeValues.emit(value);
      } else {
        this.onChangeValues.emit();
      }
    });
  }

  // if step changes reinit the component
  ngOnChanges(changes: SimpleChanges) {
    // Check if 'step' property is in the changes object and if it has changed
    if (changes['onboardingFlowStep'] && (changes['onboardingFlowStep'].currentValue !== changes['onboardingFlowStep'].previousValue)) {
      this.setupValidation();
      this.inputControl.reset(); // This will clear the input field
    }
  }

  setupValidation() {
    const validators = [];
    const asyncValidators = [];

    if (this.onboardingFlowStep.validation) {
      if (this.onboardingFlowStep.validation.required) {
        validators.push(Validators.required);
      }

      if (this.onboardingFlowStep.validation.email) {
        validators.push(Validators.email);
      }

      if (this.onboardingFlowStep.validation.totalumName) {
        asyncValidators.push(this.totalumNameChecker.bind(this));
      }
    }

    this.inputControl.setAsyncValidators(asyncValidators);
    this.inputControl.setValidators(validators);
    this.inputControl.updateValueAndValidity();
  }

  setFormError(error: ValidationErrors) {
    this.inputControl.setErrors(error);
  }

  totalumNameChecker(control: AbstractControl): Observable<ValidationErrors | null> {
    this.inputLoader = true;
    return of(control.value).pipe(
      delay(2000), // Simulate network delay for demonstration
      switchMap(value => {
        // Execute reCAPTCHA and then check instance name asynchronously
        return this.recaptchaV3Service.execute('moveInstanceFilesToAnother').pipe(
          switchMap(recaptchaToken => this.apiSdkService.checkInstanceNameIfExists(value, recaptchaToken))
        );
      }),
      map(result => {
        this.inputLoader = false;
        // If your API response structure includes a specific way to denote errors, handle it here
        if (result && result.error && result.error.errors) {
          // If the result has an error field, throw an error to jump to the catchError block
          throw result;
        }
        // Assuming result.success.data being truthy means the validation passed
        if (result && result.success && result.success.data) {
          this.onChangeValues.emit(control.value);
          return null; // No validation errors
        }
        return null; // Or handle other cases as needed
      }),
      catchError(err => {
        // Handle the thrown error from the map block or any other errors
        this.inputLoader = false;
        let error = null;
        const errorMessage = err.error?.errors || err?.message;
        
        // Match the error message and set the form control errors accordingly
        if (errorMessage === 'Error: INVALID_PATTERN') {
          error = { totalumNameInvalid: true };
        } else if (errorMessage === 'Error: ALREADY_EXISTS') {
          error = { totalumNameExists: true };
        } else if (errorMessage === 'Error: TOO_SHORT') {
          error = { totalumNameShort: true };
        } else if (errorMessage === 'Error: TOO_LONG') {
          error = { totalumNameLong: true };
        }
        
        return of(error); // Return the error object to be used by the form control
      }),
      tap({
        complete: () => this.inputLoader = false // Ensure loader is hidden when the observable completes
      })
    );
  }
  

  totalumNameSetter(inputValue: string) {
    if (inputValue && typeof inputValue === 'string') {
      const transformedValue = inputValue.toLowerCase().replace(/\s+/g, '-');
      this.inputControl.setValue(transformedValue, { emitEvent: false }); // Prevent infinite loop
    }
  }
}